/*
 * SoundTouch JS audio processing library
 * Copyright (c) Olli Parviainen
 * Copyright (c) Ryan Berdeen
 * Copyright (c) Jakub Fiala
 * Copyright (c) Steve 'Cutter' Blades
 *
 * This library is free software; you can redistribute it and/or
 * modify it under the terms of the GNU Lesser General Public
 * License as published by the Free Software Foundation; either
 * version 2.1 of the License, or (at your option) any later version.
 *
 * This library is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the GNU
 * Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public
 * License along with this library; if not, write to the Free Software
 * Foundation, Inc., 59 Temple Place, Suite 330, Boston, MA  02111-1307  USA
 */

import SoundTouch from './SoundTouch'
import WebAudioBufferSource from './WebAudioBufferSource'

export default class SimpleFilter {
  private source: WebAudioBufferSource
  private historyBufferSize: number
  private outputBufferPosition: number
  private _sourcePosition: number
  private _position: number
  private _pipe: SoundTouch
  private onEndCb: () => void

  constructor(source: WebAudioBufferSource, pipe: SoundTouch, onEnd: () => void) {
    this._pipe = pipe
    this.onEndCb = onEnd
    this.source = source
    this.historyBufferSize = 22050
    this._sourcePosition = 0
    this.outputBufferPosition = 0
    this._position = 0
  }
    
  get pipe() {
    return this._pipe
  }

  get inputBuffer() {
    return this._pipe.inputBuffer
  }

  get outputBuffer() {
    return this._pipe.outputBuffer
  }

  get position() {
    return this._position
  }

  set position(position) {
    if (position > this._position) {
      throw new RangeError(
        'New position may not be greater than current position',
      )
    }
    const newOutputBufferPosition =
      this.outputBufferPosition - (this._position - position)
    if (newOutputBufferPosition < 0) {
      throw new RangeError('New position falls outside of history buffer')
    }
    this.outputBufferPosition = newOutputBufferPosition
    this._position = position
  }

  onEnd() {
    this.onEndCb()
  }

  fillOutputBuffer(numFrames: number) {
    while (this.outputBuffer.frameCount < numFrames) {
      // TODO hardcoded buffer size
      const numInputFrames = 8192 * 2 - this.inputBuffer.frameCount

      this.fillInputBuffer(numInputFrames)

      if (this.inputBuffer.frameCount < 8192 * 2) {
        break
        // TODO: flush pipe
      }
      this._pipe.process()
    }
  }


  fillInputBuffer(numFrames: number) {
    const samples = new Float32Array(numFrames * 2)
    const numFramesExtracted = this.source.extract(
      samples,
      numFrames,
      this._sourcePosition,
    )
    this._sourcePosition += numFramesExtracted
    this.inputBuffer.putSamples(samples, 0, numFramesExtracted)
  }

  extract(target: Float32Array, numFrames: number) {
    this.fillOutputBuffer(this.outputBufferPosition + numFrames)

    const numFramesExtracted = Math.min(
      numFrames,
      this.outputBuffer.frameCount - this.outputBufferPosition,
    )
    this.outputBuffer.extract(
      target,
      this.outputBufferPosition,
      numFramesExtracted,
    )

    const currentFrames = this.outputBufferPosition + numFramesExtracted
    this.outputBufferPosition = Math.min(this.historyBufferSize, currentFrames)
    this.outputBuffer.receive(
      Math.max(currentFrames - this.historyBufferSize, 0),
    )

    this._position += numFramesExtracted
    return numFramesExtracted
  }

  clear() {
    this._pipe.clear()
    this.outputBufferPosition = 0
  }
}
